/**
 * CustomSizes.jsx
 * A size of your own, for everything the platform presets do not cover.
 *
 * The catalogue in utils/formats.js answers "what does Instagram want?". This
 * answers the other question — "I need it 800 wide for the website" — and the
 * two are deliberately kept apart on the screen, because a size somebody typed
 * themselves is not a placement and should not sit in a list of them.
 *
 * Sizes you add are remembered between visits. Somebody who needs 1600x900 for
 * their blog needs it again next week, and retyping it every time is exactly
 * the sort of small tax this app exists to remove.
 *
 * Everything downstream treats these as ordinary presets, so they crop, pad,
 * convert, download and save exactly like the built-in ones.
 *
 * Dependencies: utils/formats.js, utils/image.js, components/Icons.jsx
 */

import { useState } from 'react';
import { CUSTOM_BOUNDS, checkCustomSize, describeShape, makeCustomSize } from '../utils/formats.js';
import { planExact } from '../utils/image.js';
import { Crop, Plus, Warning } from './Icons.jsx';

export default function CustomSizes({ sources, customs, setCustoms, fit, mime, busy }) {
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [label, setLabel] = useState('');
  // Typing one number and having the other follow, so "800 wide, same shape"
  // takes one field instead of a calculator.
  const [lock, setLock] = useState(false);
  const [problem, setProblem] = useState(null);

  // The shape is taken from the first picture. With a mixed batch that is a
  // choice rather than a rule, so the hint names the picture it used.
  const shapeSource = sources[0] ?? null;
  const ratio = shapeSource ? shapeSource.width / shapeSource.height : null;
  const mixedShapes =
    sources.length > 1 &&
    sources.some((s) => Math.abs(s.width / s.height - ratio) > 0.01);

  const fromWidth = (value) =>
    ratio && value ? String(Math.max(1, Math.round(Number(value) / ratio))) : '';
  const fromHeight = (value) =>
    ratio && value ? String(Math.max(1, Math.round(Number(value) * ratio))) : '';

  function typeWidth(value) {
    setWidth(value);
    if (lock && ratio) setHeight(fromWidth(value));
  }

  function typeHeight(value) {
    setHeight(value);
    if (lock && ratio) setWidth(fromHeight(value));
  }

  function toggleLock() {
    const next = !lock;
    setLock(next);
    // Turning it on should act on what is already typed, not wait for the next
    // keystroke — otherwise the box is ticked and nothing appears to happen.
    if (next && ratio) {
      if (width) setHeight(fromWidth(width));
      else if (height) setWidth(fromHeight(height));
    }
  }

  // Checked as you type so the form can say "you already have that" before the
  // button is pressed, not as an error afterwards.
  const check = checkCustomSize(width, height, customs);

  function add(e) {
    e?.preventDefault();
    if (!check.ok) {
      setProblem(check.error);
      return;
    }
    setProblem(null);
    setCustoms((current) => [...current, makeCustomSize({ width, height, label })]);
    setWidth('');
    setHeight('');
    setLabel('');
  }

  const toggle = (id) =>
    setCustoms((current) => current.map((c) => (c.id === id ? { ...c, on: !c.on } : c)));

  const remove = (id) => setCustoms((current) => current.filter((c) => c.id !== id));

  const chosen = customs.filter((c) => c.on).length;

  return (
    <section className="card">
      <div className="card__head">
        <span className="tile">
          <Crop size={21} />
        </span>
        <div className="card__head-text">
          <h2>Your own size</h2>
          <p>
            Any width and height you like — a website banner, a print, an email header, whatever the
            presets above do not have. They are kept for next time.
          </p>
        </div>
        {customs.length > 0 && (
          <div className="row__end">
            <button
              className="btn btn--sm"
              onClick={() => setCustoms([])}
              disabled={busy}
              title="Forget every size you have added"
            >
              Clear the list
            </button>
          </div>
        )}
      </div>

      <form className="inline-form" onSubmit={add} style={{ borderTop: 'none', paddingTop: 0 }}>
        <div className="field">
          <label htmlFor="custom-width">Width</label>
          <input
            id="custom-width"
            type="number"
            inputMode="numeric"
            min={CUSTOM_BOUNDS.min}
            max={CUSTOM_BOUNDS.max}
            placeholder="1080"
            value={width}
            onChange={(e) => typeWidth(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="field">
          <label htmlFor="custom-height">Height</label>
          <input
            id="custom-height"
            type="number"
            inputMode="numeric"
            min={CUSTOM_BOUNDS.min}
            max={CUSTOM_BOUNDS.max}
            placeholder="1350"
            value={height}
            onChange={(e) => typeHeight(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="field" style={{ flex: '1 1 200px' }}>
          <label htmlFor="custom-label">Call it (optional)</label>
          <input
            id="custom-label"
            type="text"
            placeholder="Website banner"
            maxLength={40}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            disabled={busy}
          />
        </div>
        <button className="btn btn--primary" type="submit" disabled={busy || !check.ok}>
          <Plus size={15} /> Add this size
        </button>
      </form>

      {ratio && (
        <label
          className={`row row--pick${lock ? ' is-picked' : ''}`}
          style={{ marginTop: 12, cursor: 'pointer' }}
        >
          <input type="checkbox" checked={lock} onChange={toggleLock} disabled={busy} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="row__label">Keep the shape of the picture</div>
            <div className="row__sub">
              Type one number and the other follows, so nothing is cropped or padded
              {mixedShapes
                ? ` — worked out from ${shapeSource.file.name}, which is ${shapeSource.width}×${shapeSource.height}. The others are a different shape.`
                : ` (${shapeSource.width}×${shapeSource.height}).`}
            </div>
          </div>
        </label>
      )}

      {problem && (
        <div className="notice notice--error notice--compact" style={{ marginTop: 12 }}>
          <Warning />
          <span>{problem}</span>
        </div>
      )}

      {!problem && check.note && (
        <p className="hint" style={{ marginTop: 10 }}>
          {check.note}
        </p>
      )}

      {customs.length === 0 ? (
        <p className="hint" style={{ marginTop: 12 }}>
          Nothing of your own yet. Between {CUSTOM_BOUNDS.min} and {CUSTOM_BOUNDS.max} pixels on
          each side.
        </p>
      ) : (
        <div className="rows" style={{ marginTop: 16 }}>
          {customs.map((custom) => {
            // The same arithmetic the placement list does: what this batch
            // would lose at this shape, counted rather than guessed at.
            const plans = custom.on
              ? sources.map((s) =>
                  planExact({ width: s.width, height: s.height, type: s.file.type }, custom, {
                    fit,
                    mime,
                  }),
                )
              : [];
            const cropped = plans.filter((p) => p.heavyCrop).length;
            const upscaled = plans.filter((p) => p.upscaled).length;

            return (
              <div
                className={`row row--pick${custom.on ? ' is-picked' : ''}`}
                key={custom.id}
              >
                <input
                  type="checkbox"
                  id={`custom-size-${custom.id}`}
                  checked={custom.on}
                  onChange={() => toggle(custom.id)}
                  disabled={busy}
                />
                <label
                  htmlFor={`custom-size-${custom.id}`}
                  style={{ minWidth: 0, flex: 1, cursor: 'pointer', marginBottom: 0 }}
                >
                  <div className="row__label">
                    {custom.label || 'Your own size'}
                    <span className="badge">
                      {custom.width}×{custom.height}
                    </span>
                    <span className="badge">{describeShape(custom.width / custom.height)}</span>
                  </div>

                  {cropped > 0 && (
                    <div className="row__sub delta--down">
                      {cropped === sources.length ? 'Every picture' : `${cropped} of ${sources.length}`}{' '}
                      would lose a lot to cropping at this shape.
                    </div>
                  )}
                  {upscaled > 0 && (
                    <div className="row__sub delta--down">
                      {upscaled === sources.length
                        ? 'Every picture'
                        : `${upscaled} of ${sources.length}`}{' '}
                      would be enlarged, so will look softer.
                    </div>
                  )}
                </label>

                <button
                  className="btn btn--sm"
                  onClick={() => remove(custom.id)}
                  disabled={busy}
                  title="Forget this size"
                >
                  Remove
                </button>
              </div>
            );
          })}
        </div>
      )}

      {customs.length > 0 && (
        <p className="hint" style={{ marginTop: 12 }}>
          {chosen === 0
            ? 'None of your own sizes are ticked, so none will be made.'
            : `${chosen} of your own size${chosen === 1 ? '' : 's'} will be made alongside the placements above.`}
        </p>
      )}
    </section>
  );
}
